import { GenericAlert, InputState } from "@studio3t/generic-components";
import { useEffect, useState } from "react";
import { jobsApi, workspacesApi } from "@/api/client";
import type { JobMetadata, WorkspaceSummary } from "@/types/api";
import { NameModal } from "./NameModal";
import { workspaceNameTaken } from "./saveWorkspace";

/** Resolves `load()` into `set`, falling back to an empty list when the
 *  request fails; a result arriving after `isActive()` turns false is
 *  dropped. */
function loadInto<T>(
  load: () => Promise<T[]>,
  set: (list: T[]) => void,
  isActive: () => boolean,
) {
  load()
    .then((list) => {
      if (isActive()) set(list);
    })
    .catch(() => {
      if (isActive()) set([]);
    });
}

// Current workspace and job names for the duplicate check, fetched
// independently: an unreachable list drops its own check rather than
// blocking the save or taking the other check down with it (the server
// stays the authority).
function useNameSources(isOpen: boolean) {
  const [existing, setExisting] = useState<WorkspaceSummary[]>([]);
  const [jobs, setJobs] = useState<JobMetadata[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    const isActive = () => active;
    loadInto(() => workspacesApi.list(), setExisting, isActive);
    loadInto(() => jobsApi.list(), setJobs, isActive);
    return () => {
      active = false;
    };
  }, [isOpen]);

  return { existing, jobs };
}

/** The field warning for a name already taken, or null when it is free.
 *  One message for both sources: the holder can be a draft or a deployed
 *  pipeline (whose backing record shares its name, so telling them apart
 *  here would guess wrong anyway). */
function takenNameWarning(
  trimmed: string,
  sources: { existing: WorkspaceSummary[]; jobs: JobMetadata[] },
  excludes: { workspaceId?: string; jobName?: string | null },
) {
  const others = sources.existing.filter((w) => w.id !== excludes.workspaceId);
  const otherJobs = sources.jobs.filter((j) => j.name !== excludes.jobName);
  if (
    workspaceNameTaken(trimmed, others) ||
    workspaceNameTaken(trimmed, otherJobs)
  ) {
    return {
      state: InputState.Warning,
      messages: ["This name is already in use"],
    };
  }
  return null;
}

// Workspace name modal (Figma Modal demo 13376-42327) on the shared
// NameModal shell. Existing workspace names AND deployed job names are
// fetched on open; saving under a taken name shows a warning on the field
// and is blocked (compared trimmed, case-insensitively). Job names count
// as taken because a name identifies ONE pipeline across the studio: a
// workspace named after a live job folds into that job's row in the "Open
// pipeline" menu, and deploying it would take over the job. Reused for
// Save as / Rename / Duplicate via title/submitLabel/initialName;
// excludeWorkspaceId and excludeJobName lift the checks off the workspace
// being renamed and off the job that workspace itself deployed.
export function SaveWorkspaceModal({
  isOpen,
  onClose,
  onSave,
  title = "Save workspace",
  submitLabel = "Save",
  initialName = "",
  intro,
  placeholder = "Add a name to the pipeline",
  excludeWorkspaceId,
  excludeJobName,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Performs the save; a thrown error is shown on the field. */
  onSave: (name: string) => Promise<void>;
  title?: string;
  submitLabel?: string;
  initialName?: string;
  /** An info line above the field (the duplicate modal's explanation). */
  intro?: string;
  /** The field's placeholder, doubling as the empty-name error. */
  placeholder?: string;
  excludeWorkspaceId?: string;
  excludeJobName?: string | null;
}) {
  const sources = useNameSources(isOpen);

  return (
    <NameModal
      isOpen={isOpen}
      onClose={onClose}
      onSave={onSave}
      title={title}
      submitLabel={submitLabel}
      initialName={initialName}
      intro={
        intro && (
          <GenericAlert state="Info" icon dismissible={false} message={intro} />
        )
      }
      placeholder={placeholder}
      emptyMessage={placeholder}
      errorFallback="Failed to save the pipeline"
      testIdBase="save-workspace"
      validateName={(trimmed) =>
        takenNameWarning(trimmed, sources, {
          workspaceId: excludeWorkspaceId,
          jobName: excludeJobName,
        })
      }
    />
  );
}

// "Save your work as a draft" (Figma Modal demo 15949-170609): the scratch
// Save's name prompt.
export const SAVE_DRAFT_MODAL = {
  title: "Save your work as a draft",
  submitLabel: "Save as draft",
  placeholder: "Add a name to the draft",
} as const;

// "Duplicate this pipeline as draft" (Figma 15013-126334): the copy the
// Studio header and the pipeline lists both open the name modal with.
export const DUPLICATE_PIPELINE_MODAL = {
  title: "Duplicate this pipeline as draft",
  submitLabel: "Duplicate and open",
  intro:
    "Duplicate this pipeline, which will be saved as a draft, to save or apply any change that you are not ready to deploy yet.",
} as const;

// The scratch Deploy's name prompt: a deployed pipeline needs its name up
// front — the job's identity everywhere, with no rename once deployed.
export const DEPLOY_PIPELINE_MODAL = {
  title: "Deploy pipeline",
  submitLabel: "Deploy",
  intro:
    "Give your pipeline a name — it identifies the deployed pipeline and can't be changed later.",
} as const;

/** The name the duplicate modal proposes. */
export function duplicateName(name: string): string {
  return `${name}-CLONE`;
}
